import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { BuilderScreenBetaService } from './builder-screen.service';
import { BuilderNode, BuilderComponentType } from '../interfaces/builder-screen.models';

// Estrutura de dados de salvamento para entrada do usuário
export interface SaveComponentData {
  entityName: string;
  fieldName: string;
  value: any; // Valor inserido pelo usuário
  type: BuilderComponentType; // Tipo do componente para validação
}

// Payload completo de salvamento
export interface SaveFormData {
  timestamp: string;
  components: SaveComponentData[];
  metadata: {
    formId: string;
    userId?: string;
    version: string;
  };
}

@Injectable()
export class SaveComponentsService {

  constructor(private builderService: BuilderScreenBetaService) {}

  // Coleta todos os valores dos componentes do formulário
  collectComponentValues(): SaveComponentData[] {
    const nodes = this.builderService.state$.pipe(map(s => s.nodes));
    const componentData: SaveComponentData[] = [];
    
    // Obtém nós atuais de forma síncrona
    let currentNodes: ReadonlyArray<BuilderNode> = [];
    nodes.subscribe(n => currentNodes = n).unsubscribe();

    currentNodes.forEach((node) => {
      // Processa apenas componentes básicos com mapeamento de banco de dados
      if (this.isBasicComponent(node.type) && this.hasEntityMapping(node)) {
        const value = this.getComponentValue(node);

        componentData.push({
          entityName: (node.props as any).entityName,
          fieldName: (node.props as any).fieldName,
          value: value,
          type: node.type,
        });
      }
    });

    return componentData;
  }

  // Valida campos obrigatórios antes de salvar
  validateRequiredFields(componentData: SaveComponentData[]): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    componentData.forEach((data) => {
      if (this.isFieldRequired(data) && this.isEmpty(data.value)) {
        errors.push(`Field ${data.fieldName} is required`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Salva dados do formulário (chamada de service simulada)
  saveFormData(componentData: SaveComponentData[]): Observable<{ success: boolean; message: string }> {
    const savePayload: SaveFormData = {
      timestamp: new Date().toISOString(),
      components: componentData,
      metadata: {
        formId: `form-${Date.now()}`,
        version: "1.0",
      },
    };

    // Chamada de API simulada com delay
    return of(savePayload).pipe(
      delay(1500), // Simula delay de rede
      map(() => ({
        success: true,
        message: "Form data saved successfully",
      }))
    );
  }

  // Obtém estado atual dos nós para debug
  getNodes(): ReadonlyArray<BuilderNode> {
    let currentNodes: ReadonlyArray<BuilderNode> = [];
    this.builderService.state$.pipe(map(s => s.nodes)).subscribe(n => currentNodes = n).unsubscribe();
    return currentNodes;
  }

  private isBasicComponent(type: BuilderComponentType): boolean {
    return ['text', 'textarea', 'number', 'switch'].includes(type);
  }

  private hasEntityMapping(node: BuilderNode): boolean {
    return !!(node.props as any)?.entityName && !!(node.props as any)?.fieldName;
  }

  private getComponentValue(node: BuilderNode): any {
    // Obtém valor do elemento de input real do formulário usando data-component-id
    const element = document.querySelector(`[data-component-id="${node.id}"]`) as HTMLElement;

    if (!element) {
      console.warn(`Elemento não encontrado para o componente ${node.id}`);
      return null;
    }

    switch (node.type) {
      case 'text':
      case 'textarea':
        const textValue = (element as HTMLInputElement).value || '';
        return textValue;
      case 'number':
        // Para campos number (nz-input-number), precisamos acessar o valor de forma diferente
        // O nz-input-number renderiza um input interno com o valor
        
        // Estratégia 1: Procurar por input interno
        const inputElement = element.querySelector('input[type="text"]') as HTMLInputElement;
        if (inputElement) {
          const numValue = inputElement.value;
          if (numValue && numValue.trim() !== '') {
            const parsed = parseFloat(numValue);
            return isNaN(parsed) ? null : parsed;
          }
          return null;
        }
        
        // Estratégia 2: Procurar por qualquer input dentro do elemento
        const anyInput = element.querySelector('input') as HTMLInputElement;
        if (anyInput) {
          const numValue = anyInput.value;
          if (numValue && numValue.trim() !== '') {
            const parsed = parseFloat(numValue);
            return isNaN(parsed) ? null : parsed;
          }
        }
        
        // Estratégia 3: Tentar acessar diretamente o elemento
        const directValue = (element as any).value;
        if (directValue !== undefined && directValue !== null) {
          const parsed = parseFloat(directValue);
          return isNaN(parsed) ? null : parsed;
        }
        
        // Estratégia 4: Tentar acessar propriedades específicas do nz-input-number
        const ngModelValue = (element as any).ngModel;
        if (ngModelValue !== undefined && ngModelValue !== null) {
          const parsed = parseFloat(ngModelValue);
          return isNaN(parsed) ? null : parsed;
        }
        
        // Estratégia 5: Procurar por atributos data ou aria que possam conter o valor
        const ariaValue = element.getAttribute('aria-valuenow');
        if (ariaValue) {
          const parsed = parseFloat(ariaValue);
          return isNaN(parsed) ? null : parsed;
        }
        
        // Estratégia 6: Procurar por elementos com classes específicas do ng-zorro
        const ngZorroInput = element.querySelector('.ant-input-number-input') as HTMLInputElement;
        if (ngZorroInput) {
          const numValue = ngZorroInput.value;
          if (numValue && numValue.trim() !== '') {
            const parsed = parseFloat(numValue);
            return isNaN(parsed) ? null : parsed;
          }
        }
        
        return null;
      case 'switch':
        // Para switches (nz-switch), precisamos verificar se está checked
        // O nz-switch renderiza um input checkbox interno
        const switchInput = element.querySelector('input[type="checkbox"]') as HTMLInputElement;
        if (switchInput) {
          return switchInput.checked;
        }
        
        // Fallback: tenta acessar diretamente o elemento
        const directSwitchValue = (element as any).checked;
        if (directSwitchValue !== undefined) {
          return directSwitchValue;
        }
        
        // Fallback adicional: verifica se o elemento tem a classe 'ant-switch-checked'
        const isChecked = element.classList.contains('ant-switch-checked');
        if (isChecked) {
          return true;
        }
        
        // Verificar outras classes que possam indicar estado
        const hasCheckedClass = element.classList.contains('checked');
        const hasActiveClass = element.classList.contains('active');
        const hasOnClass = element.classList.contains('on');
        
        if (hasCheckedClass || hasActiveClass || hasOnClass) {
          return true;
        }
        
        // Verificar atributos data ou aria
        const ariaChecked = element.getAttribute('aria-checked');
        const dataChecked = element.getAttribute('data-checked');
        
        if (ariaChecked === 'true' || dataChecked === 'true') {
          return true;
        }
        
        // Verificar se o elemento tem filhos com classes específicas
        const checkedChild = element.querySelector('.ant-switch-checked, .checked, .active, .on');
        if (checkedChild) {
          return true;
        }
        
        return false;
      default:
        return null;
    }
  }

  private isFieldRequired(data: SaveComponentData): boolean {
    // Verifica se o campo está marcado como obrigatório
    let currentNodes: ReadonlyArray<BuilderNode> = [];
    this.builderService.state$.pipe(map(s => s.nodes)).subscribe(n => currentNodes = n).unsubscribe();
    const node = currentNodes.find((n) => (n.props as any)?.fieldName === data.fieldName);
    return !!(node?.props as any)?.required;
  }

  private isEmpty(value: any): boolean {
    return value === null || value === undefined || value === '';
  }
}
